/**
 * R8-F2 — a Polymarket US order's terms as placed, in the Open Orders Type
 * vocabulary international Polymarket rows use (`PolyOrderTifCell`): GTC;
 * GTS when a good-till-date expiry is the game start the row knows (the shared
 * tolerance), else the local expiry time; IOC; FOK; with a post-only marker
 * and the expiry-before-start marker. Terms the row did not carry, a duration
 * this surface does not recognise, or a good-till-date with no readable time
 * read as a dash: never a guess.
 */
import { ExpiryBeforeStartMarker } from '@/components/atoms/ExpiryBeforeStartWarning';
import { expiryPrecedesEventStart, isGtsEquivalentExpiry, tifLabelForExpiry } from '@/components/cards/orderCardModel';
import { GTD, durationLabel } from '@/lib/polymarketUs/ticketModel';
import type { UsOrderRow } from '@/lib/polymarketUs/types';

const UNKNOWN = '—';
/** Durations this surface names from the duration alone. */
const BARE: Record<string, string> = {
  TIME_IN_FORCE_GOOD_TILL_CANCEL: 'GTC',
  TIME_IN_FORCE_IMMEDIATE_OR_CANCEL: 'IOC',
  TIME_IN_FORCE_FILL_OR_KILL: 'FOK',
};

export type UsOrderTermsView = {
  label: string;
  /** The quick reference behind the label; null when nothing is known. */
  title: string | null;
  /** Derived by `expiryPrecedesEventStart` over the row's expiry; never re-derived by a layout. */
  expiresBeforeStart: boolean;
  postOnly: boolean;
};

/** The row's expiry in epoch ms, or null when it has none or it cannot be read. */
export function usOrderExpiryMs(row: Pick<UsOrderRow, 'timeInForce' | 'goodTillTime'>): number | null {
  if (row.timeInForce !== GTD || !row.goodTillTime) return null;
  const ms = Date.parse(row.goodTillTime);
  return Number.isFinite(ms) ? ms : null;
}

export function usOrderTerms(row: UsOrderRow, eventStartMs: number | null): UsOrderTermsView {
  const postOnly = row.postOnly === true;
  const tif = row.timeInForce;
  if (tif === undefined) return { label: UNKNOWN, title: 'Order terms not recorded', expiresBeforeStart: false, postOnly };
  const bare = BARE[tif];
  if (bare) return { label: bare, title: durationLabel(tif), expiresBeforeStart: false, postOnly };
  if (tif !== GTD) return { label: UNKNOWN, title: 'Order duration not recognised', expiresBeforeStart: false, postOnly };
  const expMs = usOrderExpiryMs(row);
  if (expMs === null) return { label: UNKNOWN, title: 'Good till date; expiry not recorded', expiresBeforeStart: false, postOnly };
  const exact = new Date(expMs).toLocaleString();
  return {
    label: tifLabelForExpiry(expMs, eventStartMs),
    title: isGtsEquivalentExpiry(expMs, eventStartMs)
      ? `Good till the scheduled start the order was placed with (${exact}); the venue does not move it if the schedule changes`
      : `Auto-cancels at ${exact}`,
    expiresBeforeStart: expiryPrecedesEventStart(expMs, eventStartMs),
    postOnly,
  };
}

/** The Type cell (table) or line (card) for a US order; one shape for every layout. */
const POST_ONLY = 'Post-only: rests in the book and never takes';

/** `postOnlyInTitle`: the shared Open Orders table (#1226) keeps post-only in
 * the cell's hover, as the international rows do; the standalone table shows the word. */
export function UsOrderTerms({ row, eventStartMs, postOnlyInTitle = false }: { row: UsOrderRow; eventStartMs?: number | null; postOnlyInTitle?: boolean }) {
  const terms = usOrderTerms(row, eventStartMs ?? null);
  const title = postOnlyInTitle && terms.postOnly ? [terms.title, POST_ONLY].filter(Boolean).join(' · ') : terms.title;
  return (
    <span data-testid="us-order-terms" title={title ?? undefined}>
      <span className={terms.label === UNKNOWN ? 'text-muted-foreground' : 'text-foreground'}>{terms.label}</span>
      {terms.postOnly && !postOnlyInTitle && (
        <span className="text-muted-foreground ml-1 text-[10px]" title={POST_ONLY}>post-only</span>
      )}
      <ExpiryBeforeStartMarker expiresBeforeEventStart={terms.expiresBeforeStart} />
    </span>
  );
}
